import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { track } from '../lib/analytics';
import { answerQuality, buildShareUrl } from '../lib/scoring';
import {
  createShareCard,
  downloadShareCard,
  nativeShare,
  openSocialShare,
  shareText,
  type SocialPlatform,
} from '../lib/share';
import { siteEntryUrl } from '../lib/qr';
import {
  DIMENSION_KEYS,
  type Answer,
  type AppContent,
  type AssessmentResult,
  type Mode,
  type Scenario,
} from '../lib/types';
import { CollectibleCard } from './CollectibleCard';

interface ResultsProps {
  content: AppContent;
  mode: Mode;
  result: AssessmentResult;
  scenarios: Scenario[];
  answers: Answer[];
  onExplore: () => void;
  onRetake: () => void;
}

const platforms: Array<{ id: SocialPlatform; label: string }> = [
  { id: 'x', label: 'X' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'xiaohongshu', label: '小红书' },
  { id: 'weibo', label: '微博' },
];

export function Results({
  content,
  mode,
  result,
  scenarios,
  answers,
  onExplore,
  onRetake,
}: ResultsProps) {
  const { ui } = content;
  const [copied, setCopied] = useState(false);
  const [openReadout, setOpenReadout] = useState(false);
  const [openReview, setOpenReview] = useState(false);

  const review = useMemo(
    () => answers
      .map((answer) => {
        const scenario = scenarios.find((entry) => entry.id === answer.scenarioId);
        const choice = scenario?.choices.find((entry) => entry.id === answer.choiceId);
        if (!scenario || !choice) return null;
        return { scenario, choice, quality: answerQuality(scenario, choice.id) };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
    [answers, scenarios],
  );

  const shareUrl = useMemo(
    () => buildShareUrl(
      window.location.href,
      result.typeId,
      result.total,
      result.percentile,
      content.locale.code,
    ),
    [content.locale.code, result],
  );

  const card = () => createShareCard(result, ui, mode);

  async function handleShare() {
    const blob = await card();
    try {
      if (await nativeShare(blob, result, ui, content.locale.code)) {
        track({ name: 'share_requested', locale: content.locale.code, method: 'native' });
        return;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }
    downloadShareCard(blob);
    track({ name: 'share_requested', locale: content.locale.code, method: 'download' });
  }

  async function handleDownload() {
    downloadShareCard(await card());
    track({ name: 'share_requested', locale: content.locale.code, method: 'download' });
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(`${shareText(result, ui)} ${shareUrl}`);
    setCopied(true);
    track({ name: 'share_requested', locale: content.locale.code, method: 'copy' });
    window.setTimeout(() => setCopied(false), 1800);
  }

  function handlePlatform(platform: SocialPlatform) {
    openSocialShare(platform, shareUrl, shareText(result, ui), `THE LINE — ${ui.types[result.typeId].title}`);
    track({ name: 'share_requested', locale: content.locale.code, method: platform });
  }

  return (
    <main className="result">
      <div className="result-stage">
        <CollectibleCard
          ui={ui}
          typeId={result.typeId}
          score={result.total}
          percentile={result.percentile}
          levelId={result.levelId}
          variant="full"
          roleLabel={ui.modes[mode].label}
          siteUrl={siteEntryUrl(window.location.href)}
        />
      </div>

      <div className="result-panel">
        <button type="button" className="action-primary" onClick={handleShare}>
          {ui.results.share}
          <span aria-hidden="true">↗</span>
        </button>

        <div className="action-pair">
          <button type="button" onClick={handleDownload}>{ui.results.download}</button>
          <button type="button" onClick={handleCopy}>{copied ? ui.results.copied : ui.results.copy}</button>
        </div>

        <div className="platforms">
          <span>{ui.results.shareTo}</span>
          <div>
            {platforms.map((platform) => (
              <button key={platform.id} type="button" onClick={() => handlePlatform(platform.id)}>
                {platform.label}
              </button>
            ))}
          </div>
        </div>

        {ui.results.shareKicker || ui.results.localOnly ? (
          <p className="result-note">{[ui.results.shareKicker, ui.results.localOnly].filter(Boolean).join(' ')}</p>
        ) : null}

        <button
          type="button"
          className="readout-toggle"
          aria-expanded={openReadout}
          onClick={() => setOpenReadout((value) => !value)}
        >
          {openReadout ? ui.results.hideReadout : ui.results.readout}
          <span aria-hidden="true">{openReadout ? '−' : '+'}</span>
        </button>

        <AnimatePresence initial={false}>
          {openReadout ? (
            <motion.div
              className="readout"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3>{ui.results.dimensionsHeading}</h3>
              <ul className="signal-bars">
                {DIMENSION_KEYS.map((key) => (
                  <li key={key}>
                    <span>{ui.dimensions[key]}</span>
                    <i aria-hidden="true"><b style={{ width: `${result.dimensions[key]}%` }} /></i>
                    <em>{result.dimensions[key]}</em>
                  </li>
                ))}
              </ul>

              {ui.results.referenceExplainer ? (
                <p className="readout-fine">{ui.results.referenceExplainer}</p>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>

        {review.length && ui.results.review ? (
          <>
            <button
              type="button"
              className="readout-toggle"
              aria-expanded={openReview}
              onClick={() => {
                setOpenReview((value) => !value);
                if (!openReview) {
                  track({ name: 'review_opened', locale: content.locale.code, mode });
                }
              }}
            >
              {openReview ? ui.results.hideReview : ui.results.review}
              <span aria-hidden="true">{openReview ? '−' : '+'}</span>
            </button>

            <AnimatePresence initial={false}>
              {openReview ? (
                <motion.ol
                  className="review"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                >
                  {review.map((entry, position) => (
                    <li key={entry.scenario.id}>
                      <p className="review-head">
                        <span className="review-index">{String(position + 1).padStart(2, '0')}</span>
                        <span className={`verdict-chip is-${entry.quality}`}>
                          {ui.feedback[entry.quality]}
                        </span>
                      </p>
                      <p className="review-question">{entry.scenario.question}</p>
                      <p className="review-choice">
                        <span>{ui.results.yourAnswer}</span>
                        {entry.choice.text}
                      </p>
                      <p className="review-effect">{entry.choice.effect}</p>
                      <p className="review-check">
                        <b>{ui.feedback.check}</b>
                        {entry.scenario.check}
                      </p>
                    </li>
                  ))}
                </motion.ol>
              ) : null}
            </AnimatePresence>
          </>
        ) : null}

        <div className="action-pair action-quiet">
          <button type="button" onClick={onExplore}>{ui.results.explore}</button>
          <button type="button" onClick={onRetake}>{ui.results.retake}</button>
        </div>

        {ui.results.platformNote ? <p className="result-fine">{ui.results.platformNote}</p> : null}
      </div>
    </main>
  );
}
